<template>
  <div>
    <h2 class="text-sm font-semibold text-gray-900 mb-1">Attribution Prospects</h2>
    <p class="text-xs text-gray-500 mb-1">
      Patients seen by your providers in PY {{ performanceYear }} who are not on the current attribution panel.
    </p>
    <p v-if="dataThroughDt" class="text-[10px] text-gray-400 mb-4">
      Data through {{ dataThroughDt }}
    </p>

    <!-- Controls -->
    <div class="flex flex-wrap items-end gap-4 mb-5">
      <!-- Year -->
      <div>
        <label class="block text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-1">Performance Year</label>
        <select
          v-model="performanceYear"
          @change="fetchData"
          class="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
        >
          <option v-for="y in yearOptions" :key="y" :value="y">PY {{ y }}</option>
        </select>
      </div>

      <!-- View filter -->
      <div>
        <label class="block text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-1">Show</label>
        <select
          v-model="viewFilter"
          class="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
        >
          <option value="all">All Prospects</option>
          <option value="new">Never Attributed</option>
          <option value="lost">Previously Attributed</option>
          <option value="winnable">Winnable (≥40% share)</option>
        </select>
      </div>

      <div class="flex-1" />

      <!-- Summary -->
      <div v-if="!loading && patients.length" class="flex items-center gap-4 text-sm text-gray-500">
        <span>
          <strong class="text-gray-900">{{ filteredPatients.length.toLocaleString() }}</strong>
          <span class="sensitive" v-if="filteredPatients.length !== patients.length"> of {{ patients.length.toLocaleString() }}</span>
          prospects
        </span>
        <span><strong class="text-gray-900">{{ filteredTotalCurrentCost }}</strong> current-year CCLF cost</span>
      </div>

      <!-- Export -->
      <ExportMenu :disabled="!patients.length" :exports="[{ type: 'csv', action: exportCsv }]" />
    </div>

    <!-- Loading -->
    <div v-if="loading" class="py-16 text-center text-sm text-gray-400">Loading prospects...</div>

    <!-- Error -->
    <div v-else-if="error" class="py-8 text-center text-sm text-rose-600">{{ error }}</div>

    <!-- Provider Summary Table (default view) -->
    <template v-else-if="!selectedProvider && providerSummary.length">
      <p class="text-xs text-gray-400 mb-2">Click a provider row to see their individual patients.</p>
      <div class="border border-gray-200 rounded-lg" style="max-height: 70vh; overflow: auto;">
        <table class="w-full text-left">
          <thead>
            <tr class="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
              <th v-for="col in providerColumns" :key="col.key"
                  class="px-3 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap cursor-pointer select-none hover:text-gray-800"
                  :class="{ 'text-right': col.align === 'right' }"
                  @click="toggleProviderSort(col.key)"
              >
                {{ col.label }}
                <span v-if="providerSortKey === col.key" class="ml-0.5 text-[9px] sensitive">{{ providerSortDir === 'asc' ? '▲' : '▼' }}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in sortedProviderSummary" :key="row.provider"
                class="border-t border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
                @click="selectedProvider = row.provider"
            >
              <td class="px-3 py-2 text-sm font-medium text-gray-900 whitespace-nowrap">
                <span class="inline-flex items-center gap-1 sensitive">
                  {{ row.provider }}
                  <font-awesome-icon :icon="['fas', 'chevron-right']" class="text-[8px] text-gray-400" />
                </span>
              </td>
              <td class="px-3 py-2 text-sm text-gray-900 text-right whitespace-nowrap font-medium sensitive">{{ row.patients.toLocaleString() }}</td>
              <td class="px-3 py-2 text-sm text-right whitespace-nowrap font-mono text-gray-700">{{ row.total_visits.toLocaleString() }}</td>
              <td class="px-3 py-2 text-sm text-right whitespace-nowrap font-mono text-gray-700">{{ formatPct(row.avg_share) }}</td>
              <td class="px-3 py-2 text-sm text-right whitespace-nowrap font-mono text-emerald-600 font-medium">{{ row.winnable }}</td>
              <td class="px-3 py-2 text-sm text-right whitespace-nowrap font-mono text-gray-700">{{ formatCurrency(row.current_cost) }}</td>
              <td class="px-3 py-2 text-sm text-right whitespace-nowrap font-mono font-medium" :class="row.no_visit_count ? 'text-rose-600' : 'text-gray-400'">{{ row.no_visit_count }}</td>
              <td class="px-3 py-2 text-sm text-right whitespace-nowrap font-mono text-gray-700">{{ formatCurrency(row.our_charges) }}</td>
              <td class="px-3 py-2 text-sm text-right whitespace-nowrap">
                <span class="text-emerald-600 font-medium">{{ row.awv_count }}</span>
                <span class="text-gray-400 sensitive"> / {{ row.patients }}</span>
              </td>
              <td class="px-3 py-2 text-sm text-right whitespace-nowrap">
                <span class="text-blue-600 font-medium">{{ row.prior_count }}</span>
                <span class="text-gray-400"> / </span>
                <span class="text-gray-500">{{ row.new_count }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <!-- Patient Detail Table (drilldown view) -->
    <template v-else-if="selectedProvider">
      <!-- Back + context -->
      <div class="mb-3 flex items-center gap-4">
        <button
          @click="selectedProvider = null; search = ''"
          class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <span class="text-[10px]">←</span> All Providers
        </button>
        <span class="text-sm font-medium text-gray-900 sensitive">{{ selectedProvider }}</span>
        <span class="text-sm text-gray-400">·</span>
        <span class="text-sm text-gray-500">{{ filteredPatients.length }} patients</span>

        <div class="flex-1" />

        <input
          v-model="search" data-demo-search
          type="text"
          placeholder="Search by name or ID..."
          class="sensitive w-64 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
        />
      </div>

      <div v-if="filteredPatients.length" class="border border-gray-200 rounded-lg" style="max-height: 70vh; overflow: auto;">
        <table class="w-full text-left">
          <thead>
            <tr class="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
              <th v-for="col in patientColumns" :key="col.key"
                  class="px-3 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap cursor-pointer select-none hover:text-gray-800"
                  :class="{ 'text-right': col.align === 'right', 'text-center': col.align === 'center' }"
                  :title="col.tooltip || ''"
                  @click="toggleSort(col.key)"
              >
                {{ col.label }}
                <span v-if="sortKey === col.key" class="ml-0.5 text-[9px]">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in sortedPatients" :key="patientId(p)"
                class="border-t border-gray-100 hover:bg-gray-50 transition-colors"
            >
              <td class="px-3 py-2 text-sm font-medium text-gray-900 whitespace-nowrap sensitive">{{ p.bene_last_name }}, {{ p.bene_first_name }}</td>
              <td class="px-3 py-2 text-sm text-gray-600 whitespace-nowrap font-mono sensitive">{{ p.bene_mbi_id || '—' }}</td>
              <td class="px-3 py-2 text-sm text-gray-600 text-right whitespace-nowrap sensitive">{{ p.bene_age ?? '—' }}</td>
              <td class="px-3 py-2 text-sm text-gray-600 whitespace-nowrap sensitive">{{ sexLabel(p.bene_sex_cd) }}</td>
              <td class="px-3 py-2 text-sm text-gray-900 text-right whitespace-nowrap font-medium">{{ p.our_distinct_dates ?? '—' }}</td>
              <td class="px-3 py-2 text-sm text-gray-600 whitespace-nowrap">{{ formatDate(p.our_last_visit_dt) }}</td>
              <td class="px-3 py-2 text-sm whitespace-nowrap text-center">
                <span v-if="p.had_awv" class="text-emerald-600 font-medium">Yes</span>
                <span v-else class="text-gray-400">—</span>
              </td>
              <!-- Plurality -->
              <td class="px-3 py-2 text-sm text-right whitespace-nowrap font-medium"
                  :class="shareClass(p.our_share_pct)">
                {{ formatPct(p.our_share_pct) }}
              </td>
              <td class="px-3 py-2 text-sm text-right whitespace-nowrap text-gray-500">
                {{ formatPct(p.winning_share_pct) }}
              </td>
              <td class="px-3 py-2 text-[11px] text-gray-500 whitespace-nowrap max-w-[180px] truncate" :title="p.winning_tin_name">
                {{ p.winning_tin_name || '—' }}
              </td>
              <!-- Facility / Status -->
              <td class="px-3 py-2 text-sm text-gray-600 whitespace-nowrap max-w-[180px] truncate sensitive" :title="p.facility_name">
                {{ p.facility_name || '—' }}
              </td>
              <td class="px-3 py-2 text-sm whitespace-nowrap">
                <span v-if="p.patient_status" class="text-gray-700 sensitive">{{ p.patient_status }}</span>
                <span v-else class="text-gray-400">—</span>
              </td>
              <!-- Cost / Risk -->
              <td class="px-3 py-2 text-sm text-gray-900 whitespace-nowrap">{{ p.visit_status || '—' }}</td>
              <td class="px-3 py-2 text-sm text-right whitespace-nowrap font-mono" :class="costClass(p.current_year_cclf_cost)"
                  :title="p.cost_through_dt ? ('Claims through ' + formatDate(p.cost_through_dt)) : ''">{{ formatCurrency(p.current_year_cclf_cost) }}</td>
              <td class="px-3 py-2 text-sm text-right whitespace-nowrap font-mono text-gray-600" :title="p.beur_cost_date ? ('Last known CMS BEUR cost as of ' + formatDate(p.beur_cost_date)) : ''">{{ formatCurrency(p.beur_total_cost) }}</td>
              <td class="px-3 py-2 text-sm text-right whitespace-nowrap font-mono text-gray-600">{{ p.risk_score != null ? p.risk_score.toFixed(2) : '—' }}</td>
              <!-- History -->
              <td class="px-3 py-2 text-sm whitespace-nowrap">
                <span v-if="p.prior_attributed" class="inline-block px-1.5 py-0.5 text-[10px] font-medium bg-blue-50 text-blue-700 rounded"
                      :title="'Reports: ' + p.report_count + ', Last: ' + formatDate(p.last_attributed_dt)"
                >{{ p.report_count }}× · Last {{ formatDate(p.last_attributed_dt) }}</span>
                <span v-else class="text-gray-400 text-xs">New</span>
              </td>

            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="py-8 text-center text-sm text-gray-400">No patients match filters.</div>
    </template>

    <!-- Empty -->
    <div v-else-if="!loading" class="py-16 text-center text-sm text-gray-400">
      No unattributed patients found for PY {{ performanceYear }}.
    </div>
  </div>
</template>

<script>
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue'
import { blockSensitiveEgress } from '@/utils/demoEgress'
import demoProtectedFields from '@/mixins/demoProtectedFields'

export default {
  name: 'AttributionProspects',
  components: { ExportMenu },
  mixins: [demoProtectedFields],
  // The patient name/ID search identifies real patients. Demo Mode empties it and the tagged
  // input is read-only while protected. It is not persisted.
  demoProtectedFields: ['search'],
  props: {
    entityId: { type: Number, default: null },
    careSetting: { type: String, default: '' },
  },
  data() {
    const currentYear = new Date().getFullYear();
    return {
      performanceYear: currentYear,
      selectedProvider: null,
      patients: [],
      loading: false,
      error: null,
      search: '',
      viewFilter: 'all',
      dataThroughDt: null,
      sortKey: 'our_share_pct',
      sortDir: 'desc',
      providerSortKey: 'winnable',
      providerSortDir: 'desc',
    };
  },
  computed: {
    yearOptions() {
      const y = new Date().getFullYear();
      return [y, y - 1];
    },
    providerColumns() {
      return [
        { key: 'provider', label: 'Provider' },
        { key: 'patients', label: 'Patients', align: 'right' },
        { key: 'total_visits', label: 'Visits', align: 'right' },
        { key: 'avg_share', label: 'Avg Share', align: 'right' },
        { key: 'winnable', label: 'Winnable', align: 'right' },
        { key: 'current_cost', label: 'Current-Year CCLF Cost', align: 'right' },
        { key: 'no_visit_count', label: 'No Doctor Visit', align: 'right' },
        { key: 'our_charges', label: 'Our Charges', align: 'right' },
        { key: 'awv_count', label: 'AWV', align: 'right' },
        { key: 'prior_count', label: 'Prior / New', align: 'right' },
      ];
    },
    patientColumns() {
      return [
        { key: 'bene_last_name', label: 'Name' },
        { key: 'bene_mbi_id', label: 'Patient ID' },
        { key: 'bene_age', label: 'Age', align: 'right' },
        { key: 'bene_sex_cd', label: 'Sex' },
        { key: 'our_distinct_dates', label: 'Visits', align: 'right' },
        { key: 'our_last_visit_dt', label: 'Last Visit' },
        { key: 'had_awv', label: 'AWV', align: 'center' },
        { key: 'our_share_pct', label: 'Our Share', align: 'right' },
        { key: 'winning_share_pct', label: 'Winner Share', align: 'right' },
        { key: 'winning_tin_name', label: 'Winning TIN' },
        { key: 'facility_name', label: 'Facility' },
        { key: 'patient_status', label: 'Status' },
        { key: 'visit_status', label: 'Visit Status' },
        { key: 'current_year_cclf_cost', label: 'Current-Year CCLF Cost', align: 'right', tooltip: 'Actual CCLF claim payments in the selected performance year, through the claims date shown.' },
        { key: 'beur_total_cost', label: 'Last Known BEUR Cost', align: 'right', tooltip: 'Most recent annualized CMS BEUR cost on record; blank for patients who never appeared in a BEUR report.' },
        { key: 'risk_score', label: 'Risk', align: 'right' },
        { key: 'prior_attributed', label: 'History', tooltip: 'Number of past CMS quarterly reports where this patient was attributed to this ACO, and the most recent report date' },
      ];
    },
    baseFilteredPatients() {
      let rows = this.patients;
      if (this.viewFilter === 'new') {
        rows = rows.filter(p => !p.prior_attributed);
      } else if (this.viewFilter === 'lost') {
        rows = rows.filter(p => p.prior_attributed);
      } else if (this.viewFilter === 'winnable') {
        rows = rows.filter(p => (p.our_share_pct || 0) >= 40);
      }
      return rows;
    },
    providerSummary() {
      const map = {};
      this.baseFilteredPatients.forEach(p => {
        const name = p.top_provider_name || '(No Provider)';
        if (!map[name]) {
          map[name] = {
            provider: name, patients: 0, total_visits: 0,
            our_charges: 0, current_cost: 0,
            awv_count: 0, no_visit_count: 0, prior_count: 0, new_count: 0,
            winnable: 0, share_sum: 0, share_count: 0,
          };
        }
        const r = map[name];
        r.patients++;
        r.total_visits += p.our_distinct_dates || 0;
        r.our_charges += p.our_allowed_charges || 0;
        r.current_cost += p.current_year_cclf_cost || 0;
        if (!p.had_doctor_visit) r.no_visit_count++;
        if (p.had_awv) r.awv_count++;
        if (p.prior_attributed) r.prior_count++;
        else r.new_count++;
        if ((p.our_share_pct || 0) >= 40) r.winnable++;
        if (p.our_share_pct != null) {
          r.share_sum += p.our_share_pct;
          r.share_count++;
        }
      });
      return Object.values(map).map(r => ({
        ...r,
        avg_share: r.share_count > 0 ? r.share_sum / r.share_count : null,
      }));
    },
    sortedProviderSummary() {
      const rows = [...this.providerSummary];
      const key = this.providerSortKey;
      const dir = this.providerSortDir === 'asc' ? 1 : -1;
      rows.sort((a, b) => {
        const va = a[key] ?? '', vb = b[key] ?? '';
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
        return String(va).localeCompare(String(vb)) * dir;
      });
      return rows;
    },
    filteredPatients() {
      let rows = this.baseFilteredPatients;
      if (this.selectedProvider) {
        const prov = this.selectedProvider;
        rows = rows.filter(p => (p.top_provider_name || '(No Provider)') === prov);
      }
      if (this.search) {
        const q = this.search.toLowerCase();
        rows = rows.filter(p =>
          (p.bene_last_name || '').toLowerCase().includes(q) ||
          (p.bene_first_name || '').toLowerCase().includes(q) ||
          (p.bene_mbi_id || '').toLowerCase().includes(q)
        );
      }
      return rows;
    },
    sortedPatients() {
      const rows = [...this.filteredPatients];
      const key = this.sortKey;
      const dir = this.sortDir === 'asc' ? 1 : -1;
      const numericKeys = ['bene_age', 'our_distinct_dates', 'our_share_pct', 'winning_share_pct', 'current_year_cclf_cost', 'beur_total_cost', 'risk_score', 'our_allowed_charges', 'report_count'];
      rows.sort((a, b) => {
        let va = a[key], vb = b[key];
        if (va == null) va = numericKeys.includes(key) ? -Infinity : '';
        if (vb == null) vb = numericKeys.includes(key) ? -Infinity : '';
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
        if (typeof va === 'boolean' && typeof vb === 'boolean') return (va === vb ? 0 : va ? 1 : -1) * dir;
        return String(va).localeCompare(String(vb)) * dir;
      });
      return rows;
    },
    filteredTotalCurrentCost() {
      const sum = this.filteredPatients.reduce((s, p) => s + (p.current_year_cclf_cost || 0), 0);
      return '$' + sum.toLocaleString(undefined, { maximumFractionDigits: 0 });
    },
  },
  methods: {
    async fetchData() {
      this.loading = true;
      this.error = null;
      this.search = '';
      this.selectedProvider = null;
      // Now sourced from the unified claims-pivot engine (metric 924,
      // ATTRIBUTION_PROSPECTS_FACT) in detail mode — same patient-grain rows,
      // single source of truth. PY is derived in-DA from clm_from_dt.
      const payload = {
        metric_id: 924,
        mode: 'detail',
        contract_id: 'A5069',
        clm_from_dt: `${this.performanceYear}-01-01`,
        clm_thru_dt: `${this.performanceYear}-12-31`,
        limit: 50000,
      };
      if (this.entityId) payload.entity_id = this.entityId;
      if (this.careSetting) payload.care_setting = this.careSetting;
      try {
        const { data } = await this.$axios.post('/api/claim-explorer/pivot/', payload);
        this.patients = data || [];
        // data_through_dt no longer returned by the engine; derive from rows if present.
        this.dataThroughDt = null;
      } catch (err) {
        console.error('Error fetching prospects:', err);
        this.error = 'Failed to load attribution prospects.';
      } finally {
        this.loading = false;
      }
    },
    toggleProviderSort(key) {
      if (this.providerSortKey === key) {
        this.providerSortDir = this.providerSortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.providerSortKey = key;
        this.providerSortDir = key === 'provider' ? 'asc' : 'desc';
      }
    },
    toggleSort(key) {

      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        const descKeys = ['bene_age', 'our_distinct_dates', 'our_share_pct', 'winning_share_pct', 'current_year_cclf_cost', 'beur_total_cost', 'risk_score', 'our_allowed_charges', 'report_count'];
        this.sortDir = descKeys.includes(key) ? 'desc' : 'asc';
      }
    },
    patientId(p) {
      return p.bene_mbi_id || Math.random();
    },
    sexLabel(cd) {
      if (cd === '1' || cd === 1) return 'M';
      if (cd === '2' || cd === 2) return 'F';
      return cd || '—';
    },
    formatCurrency(val) {
      if (val == null) return '—';
      return '$' + Number(val).toLocaleString(undefined, { maximumFractionDigits: 0 });
    },
    formatPct(val) {
      if (val == null) return '—';
      return Number(val).toFixed(1) + '%';
    },
    formatInt(val) {
      if (val == null) return '—';
      return Number(val).toLocaleString(undefined, { maximumFractionDigits: 0 });
    },
    formatDate(dt) {
      if (!dt) return '—';
      const s = String(dt).split('T')[0];
      const parts = s.split('-');
      if (parts.length !== 3) return s;
      return `${parts[1]}/${parts[2]}/${parts[0]}`;
    },
    costClass(val) {
      if (val == null) return 'text-gray-400';
      return val > 50000 ? 'text-rose-600' : 'text-gray-700';
    },
    shareClass(val) {
      if (val == null) return 'text-gray-400';
      if (val >= 50) return 'text-emerald-600';
      if (val >= 30) return 'text-amber-600';
      return 'text-gray-600';
    },
    exportCsv() {
      if (blockSensitiveEgress('export')) return;
      const headers = [
        'Last Name', 'First Name', 'Patient ID', 'Age', 'Sex', 'Top Provider',
        'Visits', 'First Visit', 'Last Visit', 'AWV',
        'Our Share %', 'Our Charges', 'Total PC Charges',
        'Winning TIN', 'Winning TIN Name', 'Winner Share %',
        'Visit Status', 'Current-Year CCLF Cost', 'Cost Through',
        'Last Known BEUR Cost', 'Risk Score',
        'Prior Attributed', 'Report Count', 'Last Attributed',
        'Full Dual', 'ESRD', 'Disabled', 'Death Date',
      ];
      const rows = this.filteredPatients.map(p => [
        p.bene_last_name, p.bene_first_name,
        p.bene_mbi_id || '',
        p.bene_age ?? '', this.sexLabel(p.bene_sex_cd),
        p.top_provider_name || '',
        p.our_distinct_dates ?? '', p.our_first_visit_dt || '', p.our_last_visit_dt || '',
        p.had_awv ? 'Y' : '',
        p.our_share_pct ?? '', p.our_allowed_charges ?? '', p.total_pc_charges ?? '',
        p.winning_tin || '', p.winning_tin_name || '', p.winning_share_pct ?? '',
        p.visit_status || '', p.current_year_cclf_cost ?? '', p.cost_through_dt || '',
        p.beur_total_cost ?? '', p.risk_score ?? '',
        p.prior_attributed ? 'Y' : 'N', p.report_count ?? '', p.last_attributed_dt || '',
        p.full_dual ? 'Y' : '', p.esrd ? 'Y' : '', p.disabled ? 'Y' : '',
        p.bene_death_dt ? String(p.bene_death_dt).split('T')[0] : '',
      ]);
      const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attribution_prospects_PY${this.performanceYear}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
  },
  watch: {
    careSetting() { this.fetchData(); },
    entityId() {
      this.fetchData();
    },
  },
  mounted() {
    this.fetchData();
  },
};
</script>
